import React, { useState, useEffect } from 'react';
import { Box, TextField, Button, DialogTitle, DialogContent, DialogActions, ButtonBase } from '@mui/material';
import {
  Groups as GroupsIcon,
  Person as PersonIcon,
  DirectionsRun as RunIcon,
  DirectionsBike as RideIcon,
  Pool as SwimIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { ContestType, ActivityType } from '../../models/types';
import { SegmentedControl } from '../ui/SegmentedControl';
import { DateTimeField } from '../ui/DateTimeField';

interface ContestFormProps {
  groupId: string;
  contest?: {
    name: string;
    detail: string;
    startAt: Date | string;
    endAt: Date | string;
    contestType: ContestType;
    activityType: ActivityType;
    minPace?: number;
    maxPace?: number;
    minDistance?: number;
  } | null;
  onSave: (data: {
    name: string;
    detail: string;
    startAt: string;
    endAt: string;
    groupId: string;
    contestType: ContestType;
    activityType: ActivityType;
    minPace?: number;
    maxPace?: number;
    minDistance?: number;
  }) => void;
  onCancel: () => void;
  isEdit?: boolean;
}

// Local time without a zone suffix ("YYYY-MM-DDTHH:mm"), the format DateTimeField reads and writes.
const toLocalInputValue = (date: Date | string): string => {
  const d = date instanceof Date ? date : new Date(date);
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
};

// Labels and descriptions are translation keys resolved at render time.
const FORMATS: { value: ContestType; icon: React.ReactNode }[] = [
  { value: 'Team', icon: <GroupsIcon /> },
  { value: 'Individual', icon: <PersonIcon /> },
];

const ACTIVITIES: { value: ActivityType; icon: React.ReactNode }[] = [
  { value: 'Run', icon: <RunIcon /> },
  { value: 'Ride', icon: <RideIcon /> },
  { value: 'Swim', icon: <SwimIcon /> },
];

const labelSx = { fontSize: 14, fontWeight: 500 } as const;

export const ContestForm = ({ groupId, contest, onSave, onCancel }: ContestFormProps) => {
  const { t } = useTranslation('contests');
  const activityOptions = ACTIVITIES.map(a => ({ ...a, label: t(`activityType.${a.value}`) }));
  const [form, setForm] = useState({
    name: '',
    detail: '',
    startAt: '',
    endAt: '',
    contestType: 'Team' as ContestType,
    activityType: 'Run' as ActivityType,
    minPace: undefined as number | undefined,
    maxPace: undefined as number | undefined,
    minDistance: undefined as number | undefined,
  });

  useEffect(() => {
    if (contest) {
      setForm({
        name: contest.name,
        detail: contest.detail,
        startAt: toLocalInputValue(contest.startAt),
        endAt: toLocalInputValue(contest.endAt),
        contestType: contest.contestType,
        activityType: contest.activityType,
        minPace: contest.minPace,
        maxPace: contest.maxPace,
        minDistance: contest.minDistance,
      });
    } else {
      // Default window: now through a week from now.
      const today = new Date();
      const nextWeek = new Date();
      nextWeek.setDate(today.getDate() + 7);
      setForm({
        name: '',
        detail: '',
        startAt: toLocalInputValue(today),
        endAt: toLocalInputValue(nextWeek),
        contestType: 'Team',
        activityType: 'Run',
        minPace: undefined,
        maxPace: undefined,
        minDistance: undefined,
      });
    }
  }, [contest]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave({ ...form, groupId });
  };

  const numberField = (key: 'minPace' | 'maxPace' | 'minDistance', label: string) => (
    <TextField
      size="small"
      label={label}
      type="number"
      value={form[key] || ''}
      onChange={e => setForm({ ...form, [key]: e.target.value ? parseFloat(e.target.value) : undefined })}
      inputProps={{ step: 0.1, min: 0 }}
    />
  );

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <DialogTitle sx={{ pb: 0.5, fontSize: 18, fontWeight: 600 }}>{contest ? t('contestForm.editTitle') : t('contestForm.newTitle')}</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '16px !important' }}>
        <TextField fullWidth label={t('contestForm.name')} placeholder={t('contestForm.namePlaceholder')} value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required />
        <TextField
          fullWidth
          label={t('contestForm.description')}
          placeholder={t('contestForm.descriptionPlaceholder')}
          multiline
          minRows={3}
          value={form.detail}
          onChange={e => setForm({ ...form, detail: e.target.value })}
        />

        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 1.5 }}>
          <DateTimeField label={t('contestForm.starts')} value={form.startAt} onChange={startAt => setForm({ ...form, startAt })} required />
          <DateTimeField label={t('contestForm.ends')} value={form.endAt} onChange={endAt => setForm({ ...form, endAt })} required />
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Box component="span" sx={labelSx}>{t('contestForm.format')}</Box>
          <Box role="radiogroup" aria-label={t('contestForm.format')} sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 1.25 }}>
            {FORMATS.map(f => {
              const active = form.contestType === f.value;
              return (
                <ButtonBase
                  key={f.value}
                  role="radio"
                  aria-checked={active}
                  onClick={() => setForm({ ...form, contestType: f.value })}
                  sx={{
                    textAlign: 'left',
                    justifyContent: 'flex-start',
                    alignItems: 'flex-start',
                    gap: 1.25,
                    p: 1.5,
                    borderRadius: '10px',
                    backgroundColor: active ? 'var(--accent-bg)' : 'var(--card)',
                    border: active ? '1.5px solid var(--primary)' : '1px solid var(--border)',
                    '& > svg': { fontSize: 18, mt: '1px', color: 'var(--primary)' },
                  }}
                >
                  {f.icon}
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                    <Box component="span" sx={{ fontSize: 14, fontWeight: 500 }}>{t(`contestType.${f.value}`)}</Box>
                    <Box component="span" sx={{ fontSize: 12, color: 'var(--muted)' }}>{t(`contestForm.formatDescription.${f.value}`)}</Box>
                  </Box>
                </ButtonBase>
              );
            })}
          </Box>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Box component="span" sx={labelSx}>{t('contestForm.activity')}</Box>
          <SegmentedControl ariaLabel={t('contestForm.activity')} options={activityOptions} value={form.activityType} onChange={activityType => setForm({ ...form, activityType })} />
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, border: '1px dashed var(--border)', borderRadius: '10px', p: 1.75 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
            <Box component="span" sx={labelSx}>{t('contestForm.qualifyingRules')}</Box>
            <Box component="span" sx={{ fontSize: 12, color: 'var(--muted)' }}>{t('contestForm.optional')}</Box>
          </Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 1.25 }}>
            {numberField('minPace', t('contestForm.minPace'))}
            {numberField('maxPace', t('contestForm.maxPace'))}
            {numberField('minDistance', t('contestForm.minDistance'))}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 1.75, borderTop: '1px solid var(--border)' }}>
        <Button variant="outlined" onClick={onCancel}>{t('common:actions.cancel')}</Button>
        <Button type="submit" variant="contained">
          {contest ? t('contestForm.saveChanges') : t('contestForm.create')}
        </Button>
      </DialogActions>
    </Box>
  );
};
